// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Rafael Pereira Satyro

import { useRef } from 'react';
import { AppIcon } from '@loa/ui-components';

type FileUploadFieldProps = Readonly<{
  fieldKey: string;
  label?: string;
  files?: File[];
  onAddFile?: (fieldKey: string, file: File) => void;
  onRemoveFile?: (fieldKey: string, index?: number) => void;
}>;

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function FileUploadField({
  fieldKey,
  label = 'Upload Documents',
  files,
  onAddFile,
  onRemoveFile,
}: FileUploadFieldProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  return (
    <div className="flex flex-col gap-sm">
      <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
        {label}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.xls,.xlsx,.doc,.docx,.csv,.png,.jpg,.jpeg"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onAddFile?.(fieldKey, f);
          if (fileInputRef.current) fileInputRef.current.value = '';
        }}
      />

      <button
        type="button"
        onClick={() => fileInputRef.current?.click()}
        className="h-[48px] px-lg w-fit border border-purple-primary rounded-button bg-white hover:bg-lilac-05 transition-colors cursor-pointer"
      >
        <div className="text-purple-primary font-sans text-body-02 font-medium leading-normal capitalize">
          Upload Document
        </div>
      </button>

      <div className="text-grey-04 font-sans text-body-03 font-normal leading-normal">
        Accepted file type: PDF, Word, Excel, JPG, PNG
      </div>

      {files && files.length > 0 && (
        <div className="flex flex-col gap-sm">
          {files.map((file, index) => (
            <div
              key={`${file.name}-${index}`}
              className="flex items-center justify-between px-lg py-md rounded-lg border border-[#A3A3A3] bg-white self-stretch"
            >
              <div className="flex items-center gap-sm">
                <div className="shrink-0">
                  <AppIcon name="file document" size={32} color="#E7000B" />
                </div>
                <div className="flex flex-col gap-[2px]">
                  <div className="text-black font-sans text-body-03 font-medium leading-normal">
                    {file.name}
                  </div>
                  <div className="text-grey-04 font-sans text-body-04 font-normal leading-normal">
                    {formatFileSize(file.size)}
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => onRemoveFile?.(fieldKey, index)}
                className="shrink-0 cursor-pointer bg-transparent border-none p-0 flex items-center"
                aria-label="Remove file"
              >
                <AppIcon name="close circle outline" size={24} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
